import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import {
  BankStatement,
  CUSTOM_LABEL_OPTION,
  DocType,
  DocTypeChoice,
  TrackedFileDocument,
} from "@/types/statement";
import { statementService } from "@/services/statements";
import { borrowerService } from "@/services/borrowers";
import { queryKeys } from "@/queryKeys";
import { useBorrowerQuery } from "./queries";

interface UseStatementsReturn {
  statements: BankStatement[];
  documents: TrackedFileDocument[];

  // Pending changes
  pendingFiles: File[];
  pendingDeletes: number[];
  pendingDocTypes: Record<number, DocType>;
  hasPendingChanges: boolean;
  addPendingFiles: (files: File[]) => void;
  removePendingFile: (index: number) => void;
  addPendingDelete: (statementId: number) => void;
  removePendingDelete: (statementId: number) => void;
  setPendingDocType: (documentId: number, docType: DocTypeChoice) => void;

  // Commit / discard
  save: (overrideBorrowerId?: number) => Promise<void>;
  discard: () => void;

  // State
  uploading: boolean;
  error: string | null;

  // Immediate actions
  view: (statementId: number) => Promise<void>;
  reprocess: (statementId: number) => Promise<void>;
}

export const useStatements = (borrowerId: number | null): UseStatementsReturn => {
  const queryClient = useQueryClient();
  const { data: borrower } = useBorrowerQuery(borrowerId);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingDeletes, setPendingDeletes] = useState<number[]>([]);
  const [pendingDocTypes, setPendingDocTypes] = useState<Record<number, DocType>>({});

  const statements: BankStatement[] = useMemo(
    () => (borrowerId != null ? (borrower?.statements ?? []) : []),
    [borrowerId, borrower?.statements]
  );
  const documents: TrackedFileDocument[] = useMemo(
    () => (borrowerId != null ? (borrower?.documents ?? []) : []),
    [borrowerId, borrower?.documents]
  );

  const hasPendingChanges = useMemo(
    () =>
      pendingFiles.length > 0 ||
      pendingDeletes.length > 0 ||
      Object.keys(pendingDocTypes).length > 0,
    [pendingFiles.length, pendingDeletes.length, pendingDocTypes]
  );

  const addPendingFiles = useCallback((files: File[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addPendingDelete = useCallback((statementId: number) => {
    setPendingDeletes((prev) => (prev.includes(statementId) ? prev : [...prev, statementId]));
  }, []);

  const removePendingDelete = useCallback((statementId: number) => {
    setPendingDeletes((prev) => prev.filter((id) => id !== statementId));
  }, []);

  const setPendingDocType = useCallback(
    (documentId: number, docType: DocTypeChoice) => {
      const doc = documents.find((d) => d.id === documentId);
      // A named "Other" file is at its name: re-picking plain Other is a change (it drops it).
      const current = doc?.custom_label ? CUSTOM_LABEL_OPTION : (doc?.doc_type ?? "unknown");
      setPendingDocTypes((prev) => {
        const next = { ...prev };
        if (docType === current || docType === CUSTOM_LABEL_OPTION) {
          delete next[documentId];
        } else {
          next[documentId] = docType;
        }
        return next;
      });
    },
    [documents]
  );

  const invalidateBorrower = useCallback(() => {
    if (borrowerId != null) {
      queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
    }
  }, [queryClient, borrowerId]);

  const uploadMutation = useMutation({
    mutationFn: ({ borrowerId: id, files }: { borrowerId: number; files: File[] }) =>
      borrowerService.uploadStatements(id, files),
    onSuccess: (_, { borrowerId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
    },
  });

  const save = useCallback(
    async (overrideBorrowerId?: number) => {
      const targetId = overrideBorrowerId ?? borrowerId;
      if (targetId == null) return;

      setUploading(true);
      setError(null);
      try {
        for (const statementId of pendingDeletes) {
          await statementService.delete(statementId);
        }

        const typeUpdates = Object.entries(pendingDocTypes).map(([fileId, docType]) => ({
          file_id: Number(fileId),
          doc_type: docType,
        }));
        if (typeUpdates.length > 0) {
          await borrowerService.updateDocumentTypes(targetId, { updates: typeUpdates });
        }

        if (pendingFiles.length > 0) {
          await uploadMutation.mutateAsync({
            borrowerId: targetId,
            files: pendingFiles,
          });
        }

        setPendingFiles([]);
        setPendingDeletes([]);
        setPendingDocTypes({});
        invalidateBorrower();
      } catch (err: unknown) {
        setError((err as Error)?.message ?? "Failed to save changes");
      } finally {
        setUploading(false);
      }
    },
    [borrowerId, invalidateBorrower, pendingDeletes, pendingDocTypes, pendingFiles, uploadMutation]
  );

  const discard = useCallback(() => {
    setPendingFiles([]);
    setPendingDeletes([]);
    setPendingDocTypes({});
    setError(null);
  }, []);

  const view = useCallback(async (statementId: number) => {
    try {
      const url = await statementService.download(statementId);
      window.open(url, "_blank", "noopener");
    } catch {
      // download failure — user can retry
    }
  }, []);

  const reprocess = useCallback(
    async (statementId: number) => {
      try {
        await statementService.reprocess(statementId);
        invalidateBorrower();
      } catch {
        // reprocess failure — user can retry
      }
    },
    [invalidateBorrower]
  );

  return {
    statements,
    documents,
    pendingFiles,
    pendingDeletes,
    pendingDocTypes,
    hasPendingChanges,
    addPendingFiles,
    removePendingFile,
    addPendingDelete,
    removePendingDelete,
    setPendingDocType,
    save,
    discard,
    uploading,
    error,
    view,
    reprocess,
  };
};
